import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Loader2, AlertCircle, CreditCard, User, Mail, Phone, ShoppingBag, Truck, Store, Gift, Copy, Check, Plus, Minus, Heart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import GoogleMapsDistance from './GoogleMapsDistance';
import {
  getUserCoupons,
  validateCouponForCheckout,
  calculateCouponDiscount,
  useCoupon,
  getCouponInstructions
} from '../utils/couponManager';
import { isOrderingAllowed, getCurrentOmanTime, getOrderingHoursMessage, isTuesday, isWomensDayOffer, getHeartPizzaAllowance } from '../utils/orderingTime';
import HeartFlavourPicker from './HeartFlavourPicker';
import { Button } from './ui/button';
import { Input } from './ui/input';

const CheckoutFlow = ({ cart, onPlaceOrder, onBackToMenu, scrollContainerRef, heartPizzaChoice, onHeartPizzaChoiceChange, onUpdateQuantity, onRemoveItem, heartFlavours = [], pizzaFlavours = [], onHeartFlavourChange }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [currentStep, setCurrentStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState(() => {
    try { return localStorage.getItem('wow_pizza_delivery_type') || ''; } catch (e) { return ''; }
  });

  // Scroll to top when checkout flow opens
  useEffect(() => {
    scrollToTop();
  }, []);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [bogoDiscount, setBogoDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [userCoupons, setUserCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showCouponSelector, setShowCouponSelector] = useState(false);
  const [distance, setDistance] = useState(0);
  const [deliveryAvailable, setDeliveryAvailable] = useState(true); // Add this state
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [nearestBranch, setNearestBranch] = useState(null);
  const [addressDetails, setAddressDetails] = useState(() => {
    try {
      const saved = localStorage.getItem('wow_pizza_address');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return { wayNumber: '', buildingNumber: '', flatNumber: '', floor: '', additionalInfo: '' };
  });
  const [customerInfo, setCustomerInfo] = useState(() => {
    try {
      const saved = localStorage.getItem('wow_pizza_customer');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return { name: '', email: '', phone: '' };
  });
  const [paymentMethod, setPaymentMethod] = useState(() => {
    try { return localStorage.getItem('wow_pizza_payment') || ''; } catch (e) { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  const [selectedPickupBranchId, setSelectedPickupBranchId] = useState(() => {
    try { return localStorage.getItem('wow_pizza_pickup_branch') || 'amerat'; } catch (e) { return 'amerat'; }
  });
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressSearchTrigger, setAddressSearchTrigger] = useState(0);
  const [savedLocationPin] = useState(() => {
    try {
      const saved = localStorage.getItem('wow_pizza_location');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });
  const [selectedLocationForSave, setSelectedLocationForSave] = useState(null);

  const handleLocationSelect = (lat, lng, address) => {
    setSelectedLocationForSave({ lat, lng, address });
  };

  // WOW Pizza shop location and contact info
  const WOW_PIZZA_BRANCHES = [
    { id: 'amerat', name: 'Al Amarat Branch', address: 'Al Amarat - Phase 5, between Omani Shiwa Restaurant & Red Roaster', phone: '+968 99236352 / 24870085', name_ar: 'فرع العامرات', address_ar: 'العامرات - المرحلة 5، بين مطعم العُماني شيوا ومقهى ريد روستر' },
    { id: 'qurum', name: 'Qurum Branch', address: 'Qurum Commercial Centre, Opposite Lulu Hypermarket, Qurum, Muscat', phone: '+968 92111965', name_ar: 'فرع القرم', address_ar: 'المركز التجاري، القرم، مسقط' },
    { id: 'khuwair', name: 'Al Khuwair Branch', address: 'Near Badr Al Samaa Hospital, Behind Indi Spice Restaurant, Al Khuwair', phone: '+968 92111946', name_ar: 'فرع الخوير', address_ar: 'بالقرب من مستشفى بدر السماء، خلف مطعم إندي سبايس، الخوير' }
  ];
  const WOW_PIZZA_ADDRESS = WOW_PIZZA_BRANCHES[0].address;
  const WOW_PIZZA_PHONE = "Amerat: +968 99236352 / 24870085 \n Qurum: +968 92111965 \n Al Khuwair: +968 92111946";
  const selectedPickupBranch = WOW_PIZZA_BRANCHES.find((branch) => branch.id === selectedPickupBranchId) || WOW_PIZZA_BRANCHES[0];

  // Load user coupons on component mount
  useEffect(() => {
    const loadCoupons = async () => {
      try {
        const coupons = await getUserCoupons();
        setUserCoupons(coupons);
      } catch (error) {
        console.error('Error loading coupons:', error);
      }
    };
    loadCoupons();
  }, []);

  const isBogoItem = (item) => item.name.toLowerCase().includes('tuesday offer');
  const isLimitedOfferItem = (item) => item.name.toLowerCase().includes('limited time offer');
  const isHappyHourItem = (item) => item.name.toLowerCase().includes('happy hour')
    || (item.name_ar || '').includes('ساعة السعادة');
  const isOfferItem = (item) => isBogoItem(item) || isLimitedOfferItem(item) || isHappyHourItem(item);

  // Omani Women's Day (17-19 Oct): each regular medium/large pizza earns one free heart-shaped pizza.
  // The customer can take anywhere from 0 to that many. The choice lives in the cart modal so the
  // cart and checkout stay in sync; null means "follow the allowance".
  const womensDayLive = isWomensDayOffer();
  const heartPizzaAllowance = getHeartPizzaAllowance(cart);
  const getsWomensDayPizza = heartPizzaAllowance > 0;
  const setHeartPizzaChoice = onHeartPizzaChoiceChange;
  const heartPizzaCount = heartPizzaChoice === null
    ? heartPizzaAllowance
    : Math.min(heartPizzaChoice, heartPizzaAllowance);

  // Auto-calculate BOGO discount on Tuesday & Friday
  useEffect(() => {
    if (!isTuesday()) {
      setBogoDiscount(0);
    } else {
      const tuesdayItems = cart.filter(item => isBogoItem(item) && !isHappyHourItem(item));
      if (tuesdayItems.length === 0) {
        setBogoDiscount(0);
      } else {
        const prices = tuesdayItems
          .flatMap(item => Array(item.quantity).fill(item.price))
          .sort((a, b) => b - a);
        const discount = prices.reduce((sum, price, idx) => idx % 2 === 1 ? sum + price : sum, 0);
        setBogoDiscount(discount);
      }
    }
    // If cart has ONLY offer items (BOGO or Limited Time Offer), coupons are not allowed
    const offerOnly = cart.length > 0 && cart.every(isOfferItem);
    if (offerOnly) {
      setCouponCode('');
      setCouponDiscount(0);
      setCouponApplied(false);
      setCouponError('');
      setSelectedCoupon(null);
    }
  }, [cart]);

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateNonOfferSubtotal = () => {
    return cart
      .filter(item => !isOfferItem(item))
      .reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const hasNonBogoItems = cart.some(item => !isBogoItem(item));
  const hasNonOfferItems = cart.some(item => !isOfferItem(item));
  const hasOfferItemsInCart = cart.some(isOfferItem);
  const offerOnlyCart = hasOfferItemsInCart && !hasNonOfferItems;

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = couponDiscount + bogoDiscount;
    const delivery = deliveryType === 'delivery' ? deliveryCharge : 0;
    return Math.max(0, subtotal - discount + delivery);
  };

  const handleCouponApply = async () => {
    setCouponError('');
    const effectiveSubtotal = hasOfferItemsInCart ? calculateNonOfferSubtotal() : calculateSubtotal();

    try {
      const validation = await validateCouponForCheckout(couponCode, effectiveSubtotal);

      if (!validation.valid) {
        setCouponError(validation.error);
        return;
      }

      const discount = calculateCouponDiscount(validation.coupon, effectiveSubtotal, deliveryCharge);
      setCouponDiscount(discount);
      setSelectedCoupon(validation.coupon);
      setCouponApplied(true);
    } catch (error) {
      setCouponError('Failed to validate coupon. Please try again.');
    }
  };

  const handleCouponRemove = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponApplied(false);
    setCouponError('');
    setSelectedCoupon(null);
  };

  const handleCouponSelect = async (coupon) => {
    setCouponCode(coupon.code);
    setSelectedCoupon(coupon);
    setShowCouponSelector(false);

    // Automatically apply the selected coupon
    setCouponError('');
    const effectiveSubtotal = hasOfferItemsInCart ? calculateNonOfferSubtotal() : calculateSubtotal();

    try {
      const validation = await validateCouponForCheckout(coupon.code, effectiveSubtotal);
      if (validation.valid) {
        const discount = calculateCouponDiscount(coupon, effectiveSubtotal);
        setCouponDiscount(discount);
        setCouponApplied(true);
        setCouponError('');
        console.log('🎫 Coupon automatically applied:', coupon.code, 'Discount:', discount);
      } else {
        setCouponError(validation.error);
      }
    } catch (error) {
      setCouponError('Failed to validate coupon. Please try again.');
    }
  };

  const copyCouponCode = (couponCode) => {
    navigator.clipboard.writeText(couponCode);
    setCouponCode(couponCode);
  };

  // Update the distance change handler:
  const handleDistanceChange = (distance, charge, available, branch) => {
    setDistance(distance);
    setDeliveryCharge(charge);
    setDeliveryAvailable(available);
    setNearestBranch(branch || null);
  };

  const getAddressQueryFromDetails = () => {
    const parts = [
      addressDetails.wayNumber ? `Way ${addressDetails.wayNumber}` : '',
      addressDetails.buildingNumber ? `Building ${addressDetails.buildingNumber}` : '',
      addressDetails.additionalInfo || '',
      'Muscat',
      'Oman'
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleCalculateFromAddressDetails = () => {
    if (!addressDetails.wayNumber || !addressDetails.buildingNumber) {
      setError(t('checkout.wayNumberError'));
      return;
    }

    const query = getAddressQueryFromDetails();
    setAddressSearchQuery(query);
    setAddressSearchTrigger((prev) => prev + 1);
    setError('');
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return !!deliveryType; // Must choose delivery or pickup
    }

    if (currentStep === 2 && deliveryType === 'delivery') {
      // Step 2: map location only
      return deliveryAvailable && distance > 0;
    }

    if (currentStep === 3 && deliveryType === 'delivery') {
      // Step 3: address details
      return addressDetails.wayNumber &&
        addressDetails.buildingNumber &&
        addressDetails.flatNumber;
    }

    if (currentStep === 4) {
      // For customer info step
      return customerInfo.name && customerInfo.email && customerInfo.phone;
    }

    return true;
  };

  const getContinueBlockedReason = () => {
    if (currentStep === 1 && !deliveryType) {
      return 'Please choose Home Delivery or Self Pickup to continue.';
    }

    if (currentStep === 2 && deliveryType === 'delivery') {
      if (distance <= 0) {
        return 'Please select your location on the map or search address to calculate distance.';
      }
      if (!deliveryAvailable) {
        return 'Delivery is not available for this location (beyond 20km).';
      }
    }
    if (currentStep === 3 && deliveryType === 'delivery') {
      if (!addressDetails.wayNumber || !addressDetails.buildingNumber || !addressDetails.flatNumber) {
        return t('checkout.wayNumberRequired');
      }
    }

    if (currentStep === 4) {
      if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
        return 'Please fill in name, email, and phone.';
      }
      if (!paymentMethod) {
        return 'Please select a payment method.';
      }
    }

    return '';
  };


  // Scroll to top function
  const scrollToTop = () => {
    // If we have a scroll container ref (modal), scroll that instead of window
    if (scrollContainerRef?.current) {
      console.log('📱 Scrolling modal container to top');
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
      setTimeout(() => {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    } else {
      // Fallback to window scroll if no container ref
      console.log('🌐 Scrolling window to top');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    }
  };

  const handleNextStep = () => {
    if (!canProceedToNextStep()) {
      const reason = getContinueBlockedReason();
      if (reason) setError(reason);
      return;
    }

    // Validate current step before proceeding
    if (currentStep === 2 && deliveryType === 'delivery' && distance <= 0) {
      setError('Please select your delivery location on map or by search.');
      return;
    }
    if (currentStep === 3 && deliveryType === 'delivery') {
      if (!addressDetails.wayNumber || !addressDetails.buildingNumber || !addressDetails.flatNumber) {
        setError('Please fill in way number, building number, and flat number');
        return;
      }
    }
    if (currentStep === 4) {
      if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
        setError('Please fill in all customer information');
        return;
      }
      if (!paymentMethod) {
        setError('Please select a payment method');
        return;
      }
    }

    // Update step only after validation passes
    if (currentStep === 1) {
      if (deliveryType === 'pickup') {
        setCurrentStep(4); // Skip to customer info for pickup
      } else {
        setCurrentStep(2); // Go to map step
      }
    } else if (currentStep === 2 && deliveryType === 'delivery') {
      setCurrentStep(3); // Go to address details step
    } else if (currentStep === 3 && deliveryType === 'delivery') {
      setCurrentStep(4); // Go to customer info
    } else if (currentStep === 4) {
      setCurrentStep(5); // Go to review
    }

    setError('');

    // Scroll to top
    scrollToTop();
  };

  const handlePreviousStep = () => {
    if (currentStep === 1) {
      onBackToMenu();
    } else if (deliveryType === 'pickup' && currentStep === 4) {
      setCurrentStep(1);
    } else if (deliveryType === 'pickup' && currentStep === 5) {
      setCurrentStep(4);
    } else {
      setCurrentStep(currentStep - 1);
    }

    // Scroll to top
    scrollToTop();
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if ordering is allowed at this time
      if (!isOrderingAllowed()) {
        const currentOmanTime = getCurrentOmanTime();

        toast.error(
          `Outside Ordering Hours!`,
          {
            description: (
              <div style={{ whiteSpace: 'pre-line' }}>
                {`Current time: ${currentOmanTime} (Oman).\nOrders can only be placed ${getOrderingHoursMessage()}.\nPlease come back during our ordering hours!`}
              </div>
            ),
            duration: 6000,
          }
        );
        setError(`Ordering is not available at this time. Please come back during our ordering hours (${getOrderingHoursMessage()}).`);
        setLoading(false);
        return;
      }

      // Mark coupon as used if applied
      if (selectedCoupon && couponApplied) {
        console.log('🎫 Using coupon:', selectedCoupon.id, selectedCoupon.code);
        const couponUsed = await useCoupon(selectedCoupon.id);
        console.log('🎫 Coupon usage result:', couponUsed);
      }

      const orderData = {
        items: cart,
        deliveryType,
        pickupBranch: deliveryType === 'pickup' ? selectedPickupBranch : null,
        customerInfo,
        paymentMethod,
        subtotal: calculateSubtotal(),
        couponDiscount,
        bogoDiscount,
        deliveryCharge: deliveryType === 'delivery' ? deliveryCharge : 0,
        total: calculateTotal(),
        addressDetails: deliveryType === 'delivery' ? addressDetails : null,
        distance: deliveryType === 'delivery' ? distance : null,
        nearestBranch: deliveryType === 'delivery' ? nearestBranch : null,
        mapLocation: deliveryType === 'delivery' ? selectedLocationForSave : null,
        appliedCoupon: selectedCoupon ? selectedCoupon.code : null,
        freeHeartPizzas: heartPizzaCount,
        freeHeartFlavours: heartFlavours
      };

      await onPlaceOrder(orderData);

      // Save customer info and preferences for next time
      try {
        localStorage.setItem('wow_pizza_customer', JSON.stringify(customerInfo));
        localStorage.setItem('wow_pizza_delivery_type', deliveryType);
        localStorage.setItem('wow_pizza_payment', paymentMethod);
        if (deliveryType === 'delivery') {
          localStorage.setItem('wow_pizza_address', JSON.stringify(addressDetails));
          if (selectedLocationForSave) {
            localStorage.setItem('wow_pizza_location', JSON.stringify(selectedLocationForSave));
          }
        }
        if (deliveryType === 'pickup') {
          localStorage.setItem('wow_pizza_pickup_branch', selectedPickupBranchId);
        }
      } catch (e) {}
    } catch (err) {
      setError('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.title')}</h3>
        <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
          {cart.map((item, index) => (
            <div key={item.cartId || index} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3 border-b border-gray-100 last:border-0">
              <div className="flex items-center flex-1 min-w-0">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-lg shadow-sm flex-shrink-0 me-3" />
                )}
                <div className="min-w-0">
                  <div className="font-bold text-gray-800 leading-snug">{lang === 'ar' && item.name_ar ? item.name_ar : item.name}</div>
                  <div className="flex items-center text-sm text-gray-500 mt-0.5">
                    {(item.selectedSize || item.size) && <span className="bg-gray-200 px-1.5 py-0.5 rounded text-[10px] uppercase me-2">{item.selectedSize || item.size}</span>}
                    <span className="whitespace-nowrap" dir="ltr">{t('common.omr')} {item.price.toFixed(3)}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-end gap-x-2 gap-y-1 sm:gap-3">
                {onUpdateQuantity && (
                  <div className="flex items-center gap-1.5 sm:gap-2" dir="ltr">
                    <button
                      type="button"
                      onClick={() => onUpdateQuantity(item.cartId, item.quantity - 1)}
                      aria-label="Decrease quantity"
                      className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-red-50 hover:text-red-600"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-4 sm:w-5 text-center font-bold text-gray-800">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => onUpdateQuantity(item.cartId, item.quantity + 1)}
                      aria-label="Increase quantity"
                      className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-green-50 hover:text-green-600"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="font-bold text-sm sm:text-base text-blue-600 whitespace-nowrap sm:w-24 text-right ms-auto sm:ms-0" dir="ltr">{(item.price * item.quantity).toFixed(3)} {t('common.omr')}</div>
                {onRemoveItem && (
                  <button
                    type="button"
                    onClick={() => onRemoveItem(item.cartId)}
                    aria-label="Remove item"
                    className="text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {getsWomensDayPizza && (
            <div className="py-3 border-b border-gray-100 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center flex-1 min-w-0">
                <img src="/assets/logo/heart_pizza.jpeg" alt={t('womensDay.freeItem')} className="w-12 h-12 sm:w-14 sm:h-14 object-contain bg-white p-0.5 rounded-lg shadow-sm flex-shrink-0 me-3" />
                <div className="min-w-0">
                  <div className="font-bold text-red-700 leading-snug">
                    🎁 <Heart className="inline h-4 w-4 fill-red-600 text-red-600 align-[-2px]" /> {t('womensDay.freeItem')}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5">{t('womensDay.chooseUpTo', { max: heartPizzaAllowance })}</div>
                </div>
              </div>
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-end gap-x-2 gap-y-1 sm:gap-3">
                <div className="flex items-center gap-1.5 sm:gap-2" dir="ltr">
                  <button
                    type="button"
                    onClick={() => setHeartPizzaChoice(Math.max(0, heartPizzaCount - 1))}
                    disabled={heartPizzaCount <= 0}
                    aria-label={t('womensDay.removeOne')}
                    className="h-7 w-7 flex items-center justify-center rounded-full border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-4 sm:w-5 text-center font-bold text-gray-800">{heartPizzaCount}</span>
                  <button
                    type="button"
                    onClick={() => setHeartPizzaChoice(Math.min(heartPizzaAllowance, heartPizzaCount + 1))}
                    disabled={heartPizzaCount >= heartPizzaAllowance}
                    aria-label={t('womensDay.addOne')}
                    className="h-7 w-7 flex items-center justify-center rounded-full border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="font-bold text-green-600 whitespace-nowrap sm:w-24 text-right">{t('womensDay.free')}</div>
                {/* Same width as the bin icon on pizza rows, so the steppers line up on desktop */}
                {onRemoveItem && <span className="hidden sm:block w-4" aria-hidden="true" />}
              </div>
            </div>
            {onHeartFlavourChange && (
              <HeartFlavourPicker flavours={heartFlavours} options={pizzaFlavours} onChange={onHeartFlavourChange} />
            )}
            </div>
          )}
          {womensDayLive && !getsWomensDayPizza && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              🎁 {t('womensDay.addPizzaHint')}
            </div>
          )}
          <div className="pt-4 mt-2">
            <div className="flex justify-between items-center font-bold text-lg text-gray-900 px-1">
              <span>{t('cart.subtotal')}:</span>
              <span className="text-blue-600 text-xl" dir="ltr">{calculateSubtotal().toFixed(3)} {t('common.omr')}</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('checkout.couponCode')}</h3>
          {userCoupons.length > 0 && !offerOnlyCart && (
            <button
              onClick={() => setShowCouponSelector(!showCouponSelector)}
              className="flex items-center space-x-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              <Gift className="h-4 w-4" />
              <span className="text-sm font-medium">{t('checkout.myCoupons')} ({userCoupons.filter(c => !c.used).length})</span>
            </button>
          )}
        </div>

        {offerOnlyCart && (
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
            {t('checkout.couponsCantCombine')}
          </div>
        )}

        {bogoDiscount > 0 && hasNonBogoItems && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Coupon minimum is checked against non-offer items only ({calculateNonOfferSubtotal().toFixed(3)} OMR).
          </div>
        )}

        {/* Coupon Selector */}
        {!offerOnlyCart && showCouponSelector && (
          <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-3">{t('checkout.selectACoupon')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {userCoupons.filter(coupon => !coupon.used).map((coupon) => (
                <div
                  key={coupon.id}
                  className="p-3 bg-white rounded-lg border border-red-200 hover:border-red-300 cursor-pointer transition-colors"
                  onClick={() => handleCouponSelect(coupon)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono font-bold text-red-800">{coupon.code}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyCouponCode(coupon.code);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="text-sm font-semibold text-red-700 mb-1">
                    {coupon.type === 'percentage' && t('checkout.couponPercentOff', { discount: coupon.discount })}
                    {coupon.type === 'fixed' && t('checkout.couponFixedOff', { discount: coupon.discount })}
                    {coupon.type === 'delivery' && t('checkout.couponFreeDelivery')}
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{lang === 'ar' ? (coupon.text_ar || coupon.text || coupon.description) : (coupon.text || coupon.description)}</p>
                  <div className="space-y-1">
                    {getCouponInstructions(coupon, lang).slice(0, 2).map((instruction, index) => (
                      <div key={index} className="text-xs text-gray-500">• {instruction}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {userCoupons.filter(coupon => !coupon.used).length === 0 && (
              <p className="text-center text-gray-500 py-4">{t('checkout.noActiveCoupons')}</p>
            )}
          </div>
        )}

        {/* Manual Coupon Input */}
        {!offerOnlyCart && (
        <div className="flex space-x-2">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder={lang === 'ar' ? 'أدخل كود الكوبون' : 'Enter coupon code manually'}
            className="flex-1 min-w-0 p-2 border border-gray-300 rounded-md"
            disabled={couponApplied}
          />
          {!couponApplied ? (
            <button
              onClick={handleCouponApply}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('checkout.applyCoupon')}
            </button>
          ) : (
            <button
              onClick={handleCouponRemove}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              {lang === 'ar' ? 'إزالة' : 'Remove'}
            </button>
          )}
        </div>
        )}

        {couponError && (
          <p className="text-red-600 text-sm mt-1">{couponError}</p>
        )}

        {!offerOnlyCart && couponApplied && selectedCoupon && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-800 font-semibold">✓ Coupon Applied!</span>
              <span className="text-green-700 font-bold">-{couponDiscount.toFixed(2)} OMR</span>
            </div>
            <p className="text-sm text-green-700">{selectedCoupon.text || selectedCoupon.description}</p>
            <div className="mt-2 text-xs text-green-600">
              {getCouponInstructions(selectedCoupon).map((instruction, index) => (
                <div key={index}>• {instruction}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.chooseDeliveryType')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setDeliveryType('delivery')}
            className={`p-4 border-2 rounded-lg flex items-center space-x-3 ${deliveryType === 'delivery'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
              }`}
          >
            <Truck className="h-6 w-6 text-blue-600" />
            <div className="text-left">
              <div className="font-semibold">{t('checkout.delivery')}</div>
              <div className="text-sm text-gray-600">{lang === 'ar' ? 'سنوصل إلى عنوانك' : "We'll deliver to your address"}</div>
            </div>
          </button>

          <button
            onClick={() => setDeliveryType('pickup')}
            className={`p-4 border-2 rounded-lg flex items-center space-x-3 ${deliveryType === 'pickup'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
              }`}
          >
            <Store className="h-6 w-6 text-green-600" />
            <div className="text-left">
              <div className="font-semibold">{t('checkout.pickup')}</div>
              <div className="text-sm text-gray-600">{lang === 'ar' ? 'استلام من مطعمنا' : 'Pick up from our restaurant'}</div>
            </div>
          </button>
        </div>

        {/* Store Address Display for Self Pickup */}
        {deliveryType === 'pickup' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-800 mb-1">{t('checkout.chooseBranch')}</h4>
                <div className="space-y-2 mt-2">
                  {WOW_PIZZA_BRANCHES.map((branch) => (
                    <label key={branch.id} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pickupBranch"
                        value={branch.id}
                        checked={selectedPickupBranchId === branch.id}
                        onChange={(e) => setSelectedPickupBranchId(e.target.value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-green-700 font-medium">{lang === 'ar' && branch.name_ar ? branch.name_ar : branch.name}: {lang === 'ar' && branch.address_ar ? branch.address_ar : branch.address}</span>
                        <p className="text-sm text-green-600 mt-0.5">📞 {branch.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Step 2: map selection
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.deliveryLocationMap')}</h3>

        {/* Google Maps Distance Calculator */}
        <div className="mb-6">
          <GoogleMapsDistance
            key={`map-${currentStep}`}
            onDistanceChange={handleDistanceChange}
            onAddressResolved={handleAddressAutoFill}
            externalSearchQuery={addressSearchQuery}
            externalSearchTrigger={addressSearchTrigger}
            initialLocation={savedLocationPin}
            onLocationSelect={handleLocationSelect}
          />
        </div>

        {/* Delivery Availability Warning */}
        {distance !== null && !deliveryAvailable && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('checkout.deliveryNotAvailable')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{t('checkout.outOfRange', { distance: distance?.toFixed(1) })}</p>
                  <p className="mt-1">{t('checkout.considerPickup')}</p>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );

  // Step 3: address details
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.deliveryAddressDetails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.wayNumber')} *
            </label>
            <input
              type="text"
              value={addressDetails.wayNumber}
              onChange={(e) => setAddressDetails({ ...addressDetails, wayNumber: e.target.value })}
              className="w-full p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              placeholder={t('checkout.wayPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.buildingNumber')} *
            </label>
            <input
              type="text"
              value={addressDetails.buildingNumber}
              onChange={(e) => setAddressDetails({ ...addressDetails, buildingNumber: e.target.value })}
              className="w-full p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              placeholder={t('checkout.buildingPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.flatNumber')} *
            </label>
            <input
              type="text"
              value={addressDetails.flatNumber}
              onChange={(e) => setAddressDetails({ ...addressDetails, flatNumber: e.target.value })}
              className="w-full p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              placeholder={t('checkout.flatPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.floorNumber')}
            </label>
            <input
              type="text"
              value={addressDetails.floor}
              onChange={(e) => setAddressDetails({ ...addressDetails, floor: e.target.value })}
              className="w-full p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              placeholder={t('checkout.floorPlaceholder')}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.additionalInfo')}
            </label>
            <textarea
              value={addressDetails.additionalInfo}
              onChange={(e) => setAddressDetails({ ...addressDetails, additionalInfo: e.target.value })}
              className="w-full p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              placeholder={t('checkout.additionalInfoPlaceholder')}
              rows="3"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.step3')}</h3>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.customerName')} *
            </label>
            <div className="relative group">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                placeholder={t('checkout.namePlaceholder')}
                className="w-full pl-11 p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.customerEmail')} *
            </label>
            <div className="relative group">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                placeholder={lang === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                className="w-full pl-11 p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              {t('checkout.customerPhone')} *
            </label>
            <div className="relative group">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                placeholder={t('checkout.phonePlaceholder')}
                className="w-full pl-11 p-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.paymentMethod')}</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              name="payment"
              value="cash"
              checked={paymentMethod === 'cash'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="text-blue-600"
            />
            <CreditCard className="h-5 w-5 text-gray-600" />
            <div>
              <span>{t('checkout.cashOnDelivery')}</span>
              <p className="text-xs text-gray-500 mt-1">{lang === 'ar' ? 'جميع المعاملات عند الاستلام' : 'All transactions will be done on delivery, no online transactions'}</p>
            </div>
          </label>
          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              name="payment"
              value="debit"
              checked={paymentMethod === 'debit'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="text-blue-600"
            />
            <CreditCard className="h-5 w-5 text-gray-600" />
            <div>
              <span>{lang === 'ar' ? 'بطاقة مدين' : 'Debit Card'}</span>
              <p className="text-xs text-gray-500 mt-1">{lang === 'ar' ? 'جميع المعاملات عند الاستلام' : 'All transactions will be done on delivery, no online transactions'}</p>
            </div>
          </label>
          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer">
            <input
              type="radio"
              name="payment"
              value="credit"
              checked={paymentMethod === 'credit'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="text-blue-600"
            />
            <CreditCard className="h-5 w-5 text-gray-600" />
            <div>
              <span>{lang === 'ar' ? 'بطاقة ائتمان' : 'Credit Card'}</span>
              <p className="text-xs text-gray-500 mt-1">{lang === 'ar' ? 'جميع المعاملات عند الاستلام' : 'All transactions will be done on delivery, no online transactions'}</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
  // In CheckoutFlow.jsx, add this function:

  const handleAddressAutoFill = (autoFilledData) => {
    console.log('Auto-filling address data:', autoFilledData); // Debug log
    setAddressDetails(prev => ({
      ...prev,
      wayNumber: autoFilledData.wayNumber || prev.wayNumber,
      buildingNumber: autoFilledData.buildingNumber || prev.buildingNumber,
      // Don't auto-fill flat number or floor - user needs to enter these
      additionalInfo: autoFilledData.additionalInfo || prev.additionalInfo
    }));
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.orderSummary')}</h3>
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          {/* Every item with size, quantity and combo choices, so the customer can check the full order */}
          <ul className="space-y-2.5 pb-3 border-b border-gray-200">
            {cart.map((item, index) => (
              <li key={item.cartId || index} className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">
                    <span dir="ltr">{item.quantity}×</span> {lang === 'ar' && item.name_ar ? item.name_ar : item.name}
                    {(item.selectedSize || item.size) && (
                      <span className="ms-2 bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase align-middle">{item.selectedSize || item.size}</span>
                    )}
                  </div>
                  {item.selections && item.selections.length > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {item.selections.map(sel => (sel.items || []).join(', ')).filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <span className="whitespace-nowrap text-gray-700" dir="ltr">{(item.price * item.quantity).toFixed(3)} {t('common.omr')}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-between">
            <span>{t('cart.subtotal')}:</span>
            <span dir="ltr">{calculateSubtotal().toFixed(2)} {t('common.omr')}</span>
          </div>
          {heartPizzaCount > 0 && (
            <div className="flex justify-between items-start gap-3 text-green-600">
              <div className="min-w-0">
                <div className="font-medium">
                  🎁 <Heart className="inline h-4 w-4 fill-red-600 text-red-600 align-[-2px]" /> {t('womensDay.freeItem')} <span className="whitespace-nowrap" dir="ltr">×{heartPizzaCount}</span>
                </div>
                {heartFlavours.length > 0 && pizzaFlavours.length > 0 && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    {/* Group repeated flavours: "BBQ Chicken Pizza ×2 · Pepperoni Pizza ×1" */}
                    {[...new Set(heartFlavours)]
                      .map(id => {
                        const pizza = pizzaFlavours.find(p => p.id === id);
                        if (!pizza) return null;
                        const count = heartFlavours.filter(f => f === id).length;
                        return `${lang === 'ar' && pizza.name_ar ? pizza.name_ar : pizza.name} ×${count}`;
                      })
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                )}
              </div>
              <span className="font-semibold whitespace-nowrap">{t('womensDay.free')}</span>
            </div>
          )}
          {bogoDiscount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>{t('checkout.bogoOffer')}:</span>
              <span dir="ltr">-{bogoDiscount.toFixed(2)} {t('common.omr')}</span>
            </div>
          )}
          {couponDiscount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>{t('cart.discount')}:</span>
              <span dir="ltr">-{couponDiscount.toFixed(2)} {t('common.omr')}</span>
            </div>
          )}
          {deliveryType === 'delivery' && (
            <div className="flex justify-between">
              <span>{t('cart.deliveryFee')}:</span>
              <span dir="ltr">{deliveryCharge.toFixed(2)} {t('common.omr')}</span>
            </div>
          )}
          <div className="border-t pt-2">
            <div className="flex justify-between font-semibold text-lg">
              <span>{t('cart.total')} <span className="text-xs font-normal text-gray-500">({t('checkout.exclVAT')}):</span></span>
              <span dir="ltr">{calculateTotal().toFixed(2)} {t('common.omr')}</span>
            </div>
            <p className="text-xs text-orange-600 font-medium mt-1 text-right">* {t('checkout.vatNotIncluded')}</p>
          </div>
        </div>
        <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
          <span className="text-orange-500 text-base flex-shrink-0">⚠️</span>
          <p className="text-xs text-orange-800 leading-relaxed">
            <span className="font-bold">{t('checkout.priceExclVAT')}</span> {t('checkout.vatNote')}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">{t('checkout.customerDetails')}</h3>
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div><strong>{t('checkout.labelName')}:</strong> {customerInfo.name}</div>
          <div><strong>{t('checkout.labelEmail')}:</strong> {customerInfo.email}</div>
          <div><strong>{t('checkout.labelPhone')}:</strong> {customerInfo.phone}</div>
          <div><strong>{t('checkout.labelPayment')}:</strong> {
            paymentMethod === 'cash' ? t('checkout.cashPayment') :
              paymentMethod === 'debit' ? t('checkout.debitPayment') :
                paymentMethod === 'credit' ? t('checkout.creditPayment') :
                  paymentMethod
          }</div>
          {deliveryType === 'delivery' && (
            <>
              <div><strong>{t('checkout.labelDeliveryDistance')}:</strong> <span dir="ltr">{distance.toFixed(1)} km</span></div>
              {nearestBranch && <div><strong>{t('checkout.labelNearestBranch')}:</strong> {nearestBranch.name}</div>}
              <div><strong>{t('checkout.labelAddress')}:</strong> Way {addressDetails.wayNumber}, Building {addressDetails.buildingNumber}, Flat {addressDetails.flatNumber}</div>
              {addressDetails.floor && <div><strong>{t('checkout.labelFloor')}:</strong> {addressDetails.floor}</div>}
              {addressDetails.additionalInfo && <div><strong>{t('checkout.labelAdditionalInfo')}:</strong> {addressDetails.additionalInfo}</div>}
            </>
          )}
          {deliveryType === 'pickup' && (
            <>
              <div><strong>{t('checkout.labelPickupBranch')}:</strong> {selectedPickupBranch.name}</div>
              <div><strong>{t('checkout.labelPickupAddress')}:</strong> {selectedPickupBranch.address}</div>
              <div><strong>{t('checkout.labelContact')}:</strong> {selectedPickupBranch.phone}</div>
            </>
          )}
        </div>
      </div>

      {/* Extra safety confirmation step to protect your order */}
      <div className="p-4 rounded-lg border border-yellow-300 bg-yellow-50 flex space-x-3">
        <AlertCircle className="h-6 w-6 text-yellow-700 flex-shrink-0 mt-1" />
        <div className="text-sm text-yellow-900">
          <p className="font-semibold uppercase tracking-wide text-xs mb-1">
            {t('checkout.finalStepTitle')}
          </p>
          <p>
            {t('checkout.finalStepDesc', { phone: WOW_PIZZA_PHONE })}
          </p>
          <p className="mt-1 text-xs sm:text-sm">
            {t('checkout.finalStepNote')}
          </p>
        </div>
      </div>

      <button
        onClick={() => setShowCallConfirm(true)}
        disabled={loading}
        className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t('checkout.placingOrder')}</span>
          </>
        ) : (
          <>
            <ShoppingBag className="h-5 w-5" />
            <span>{t('checkout.placeOrder')}</span>
          </>
        )}
      </button>
    </div>
  );

  const steps = [
    {
      title: t('checkout.step1'),
      description: "Review your order and apply coupons",
      show: true
    },
    {
      title: t('checkout.step2'),
      description: "Select your delivery location on the map",
      show: deliveryType === 'delivery'
    },
    {
      title: t('checkout.addressDetails'),
      description: "Enter way, building, and flat details",
      show: deliveryType === 'delivery'
    },
    {
      title: t('checkout.customerInformation'),
      description: "Enter your contact details",
      show: true
    },
    {
      title: t('checkout.reviewPlace'),
      description: "Review and confirm your order",
      show: true
    }
  ];

  const visibleSteps = deliveryType === 'pickup'
    ? [
      { title: t('checkout.orderPickup'), description: 'Review order and select pickup' },
      { title: t('checkout.customerInfo'), description: 'Your contact and payment details' },
      { title: t('checkout.reviewPlace'), description: 'Final review and order placement' }
    ]
    : steps.filter((step, index) => {
      if (index === 0) return true; // Always show first step
      if (step.show === false) return false; // Hide if explicitly false
      if (step.show === undefined) return true; // Show if not specified
      return step.show; // Show if true
    });

  const getCurrentVisibleStep = () => {
    if (deliveryType === 'pickup') {
      // For pickup flow: 1 -> Order, 4 -> Customer, 5 -> Review
      if (currentStep === 1) return 1;
      if (currentStep === 4) return 2;
      if (currentStep === 5) return 3;
      return 1;
    }

    // Delivery flow uses the normal step counting
    let visibleIndex = 0;
    for (let i = 0; i < currentStep; i++) {
      if (visibleSteps[i]) visibleIndex++;
    }
    return visibleIndex;
  };

  return (
    <>
    <div className="w-full max-w-4xl mx-auto p-2 sm:p-6">
      {/* Progress Stepper - Optimized for narrow screens */}
      <div className="mb-8 px-2">
        <div className="flex items-center justify-between relative px-2">
          {/* Background horizontal line */}
          <div className="absolute top-4 left-[20%] right-[20%] h-0.5 bg-gray-100 -z-10"></div>

          {visibleSteps.map((step, index) => {
            const isCompleted = index < getCurrentVisibleStep() - 1;
            const isActive = index === getCurrentVisibleStep() - 1;

            return (
              <div key={index} className="flex flex-col items-center relative z-10 flex-1">
                {/* Step Circle */}
                <div
                  className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-500 shadow-sm
                    ${isCompleted ? 'bg-green-600 text-white' : isActive ? 'bg-red-600 text-white ring-4 ring-red-100 scale-110' : 'bg-gray-100 text-gray-400'}`}
                >
                  {isCompleted ? <Check className="h-5 w-5 sm:h-6 sm:w-6" /> : <span className="font-black text-sm">{index + 1}</span>}
                </div>

                {/* Compact Step Label */}
                <div className="mt-2 text-center max-w-[60px] sm:max-w-[100px]">
                  <span className={`text-[9px] sm:text-[11px] font-black uppercase tracking-tighter sm:tracking-normal leading-tight block
                    ${isActive ? 'text-red-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                    {step.title.split(' ')[0]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start animate-shake">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
          <span className="text-red-700 text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Step Content Card */}
      <div className="bg-white rounded-3xl p-4 sm:p-8 border border-gray-100 shadow-xl shadow-gray-200/50">
        <div className="min-h-[40vh]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && deliveryType === 'delivery' && renderStep2()}
          {currentStep === 3 && deliveryType === 'delivery' && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>

        {/* Navigation Actions */}
        <div className="flex items-center gap-3 sm:gap-4 mt-12 pt-8 border-t border-gray-100">
          <Button
            onClick={handlePreviousStep}
            variant="outline"
            className="flex-1 sm:flex-none min-w-0 px-3 sm:px-6 py-6 border-2 border-gray-200 text-gray-600 font-black rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95 text-xs sm:text-base uppercase tracking-wide sm:tracking-widest"
          >
            {currentStep === 1 ? (lang === 'ar' ? 'العودة للقائمة' : 'Back to Menu') : (lang === 'ar' ? 'السابق' : 'Previous')}
          </Button>

          {currentStep < 5 && (
            <Button
              onClick={handleNextStep}
              disabled={!canProceedToNextStep()}
              className={`flex-1 sm:flex-none min-w-0 px-3 sm:px-8 py-6 rounded-2xl font-black transition-all active:scale-95 text-xs sm:text-base uppercase tracking-wide sm:tracking-widest shadow-lg
                ${canProceedToNextStep()
                  ? 'bg-red-600 text-white hover:bg-red-700 shadow-red-500/20'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed border-none'
                }`}
            >
              {lang === 'ar' ? 'متابعة' : 'Continue'}
            </Button>
          )}
        </div>
        {!canProceedToNextStep() && (
          <div className="mt-3 px-1 text-sm text-amber-700 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{getContinueBlockedReason()}</span>
          </div>
        )}
      </div>
    </div>

    {/* Call-to-confirm popup shown when user taps Place Order */}
    {showCallConfirm && (
      <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border-2 border-yellow-400 p-6 space-y-4 animate-[fadeInScale_0.2s_ease-out]">
          <div className="flex items-start space-x-3">
            <div className="p-3 bg-yellow-100 rounded-full">
              <AlertCircle className="h-8 w-8 text-yellow-800" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">
                Final Step: Call To Confirm Your Order
              </h2>
              <p className="text-base text-gray-800 font-semibold">
                Please call WOW Pizza now at <span className="text-red-600 font-black">{WOW_PIZZA_PHONE}</span> to verbally confirm your order.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                We do this extra step to make sure your order is placed by a real person (not AI or automated), so your pizza is prepared correctly and without mistakes.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0">
            <button
              onClick={() => setShowCallConfirm(false)}
              className="w-full sm:w-auto px-4 py-3 rounded-xl font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => {
                setShowCallConfirm(false);
                handlePlaceOrder();
              }}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-3 rounded-xl font-black bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg shadow-red-500/30 transition-colors"
            >
              {t('checkout.iWillCallConfirm')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CheckoutFlow; 